import { ActionIcon, Box, Group, Menu, Text } from '@mantine/core';
import { IconDotsVertical, IconFolder } from '@tabler/icons-react';
import React from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { EntityWithFile, UploadedFile } from '../../hooks/file-uploads/types';
import { useFileDragDrop } from '../context/FileDragDropContext';
import { createDragElement } from '../utils/createDragElement';
import classes from '../FilesPage.module.css';

type FolderStripItemProps = {
  folder: UploadedFile;
  onOpen: (folder: EntityWithFile) => void;
  menuItems: React.ReactNode;
};

export default function FolderStripItem({ folder, onOpen, menuItems }: FolderStripItemProps) {
  const dragDropContext = useFileDragDrop();
  const isDropTarget = dragDropContext?.isDropTarget(folder.id);
  const draggable = !!dragDropContext;
  const isDragging = dragDropContext?.isDragging(folder.id);

  const handleClick = () => {
    if (dragDropContext?.draggedItem) return;
    onOpen(folder);
  };

  const handleDragStart = (e: React.DragEvent) => {
    if (!dragDropContext) return;
    e.dataTransfer.effectAllowed = 'move';
    dragDropContext.setDraggedItem(folder);
    createDragElement(folder, e);
  };

  const handleDragEnd = () => {
    if (!dragDropContext) return;
    dragDropContext.setDraggedItem(null);
    dragDropContext.setIsDraggedOver(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (dragDropContext && dragDropContext.canDropOnTarget(folder.id)) {
      e.preventDefault();
      dragDropContext.setIsDraggedOver(folder.id);
    }
  };

  const handleDragLeave = () => {
    dragDropContext?.setIsDraggedOver(null);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    if (!dragDropContext?.draggedItem || !dragDropContext.canDropOnTarget(folder.id)) {
      return;
    }
    const dragged = dragDropContext.draggedItem;
    try {
      await dragDropContext.handleMoveFile(dragged, folder.id);
    } finally {
      dragDropContext.setDraggedItem(null);
      dragDropContext.setIsDraggedOver(null);
    }
  };

  return (
    <Group
      gap="xs"
      p="xs"
      w={{ base: '100%', xs: 240 }}
      wrap="nowrap"
      bg="white"
      style={{ borderRadius: 12 }}
      className={`${classes.folderItem} ${isDragging ? classes.dragging : ''} ${isDropTarget ? classes.dropTarget : ''}`.trim()}
      draggable={draggable}
      onClick={handleClick}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-testid="folder-strip-item"
    >
      <Box className={classes.folderIconBadge}>
        <IconFolder size={18} color="var(--mantine-color-gray-7)" />
      </Box>
      <Text
        size="sm"
        fw={FONT_WEIGHTS.medium}
        c="gray.10"
        truncate
        style={{ flex: 1, minWidth: 0 }}
      >
        {folder.file.fileName}
      </Text>
      <Menu position="bottom-end">
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray.8"
            aria-label="Folder actions"
            data-testid="folder-strip-item-menu"
            onClick={e => e.stopPropagation()}
          >
            <IconDotsVertical size={18} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown onClick={e => e.stopPropagation()}>{menuItems}</Menu.Dropdown>
      </Menu>
    </Group>
  );
}
